import React, { useState, useEffect, useRef } from "react";
import FormModal from "./FormModal";
import PropTypes from "prop-types";
import { Toast } from "primereact/toast";
import { useIndicatorDefaultsStore } from "~/store/indicatorDefaultsStore";
import "./IndicatorEditModal.css";

// indicatorEntry: registry entry for the indicator
// visible: boolean
// onClose: () => void
// onSave: (indicatorInstance) => void
// chartIndicators: array of chart indicators (optional, for series-as-source)
// currentPanel: current panel ID (optional, for series-as-source)
const IndicatorEditModal = ({
  indicatorEntry,
  visible,
  onClose,
  onSave,
  chartIndicators = [],
  currentPanel = null,
  hostBody = null,
}) => {
  const toast = useRef(null);

  // Get store actions and state
  const {
    getConfigDefaults,
    getAppearanceDefaults,
    saveAllDefaults,
    saveAppearanceDefaults,
    resetAllDefaults,
    resetAppearanceDefaults,
  } = useIndicatorDefaultsStore();

  const getInitialOptions = () => {
    if (
      !indicatorEntry ||
      !indicatorEntry.defaultOptions ||
      !Array.isArray(indicatorEntry.defaultOptions)
    )
      return {};

    // Priority: instance config > custom defaults > registry defaults
    const instanceConfig = indicatorEntry.config || {};
    const customDefaults = getConfigDefaults(indicatorEntry.key) || {};

    return indicatorEntry.defaultOptions.reduce((acc, prop) => {
      acc[prop.name] =
        instanceConfig[prop.name] !== undefined
          ? instanceConfig[prop.name]
          : customDefaults[prop.name] !== undefined
            ? customDefaults[prop.name]
            : prop.defaultValue;
      return acc;
    }, {});
  };

  // Ensure appearanceConfig is { [name]: defaultValue }
  // Uses deep merge for nested objects to prevent partial object issues
  const getInitialAppearanceOptions = () => {
    if (!indicatorEntry || !indicatorEntry.defaultOptionsForAppearance)
      return {};

    // Priority: instance appearance > custom defaults > registry defaults
    const instanceAppearance = indicatorEntry.appearanceConfig || {};
    const customDefaults = getAppearanceDefaults(indicatorEntry.key) || {};

    const result = indicatorEntry.defaultOptionsForAppearance.reduce(
      (acc, prop) => {
        const defaultVal = prop.defaultValue;
        const customVal = customDefaults[prop.name];
        const instanceVal = instanceAppearance[prop.name];

        // For object types (like histogramBar, stroke), deep merge with defaults
        // This ensures partial objects don't lose properties
        if (
          defaultVal &&
          typeof defaultVal === "object" &&
          !Array.isArray(defaultVal)
        ) {
          acc[prop.name] = {
            ...defaultVal, // Start with registry defaults
            ...(customVal || {}), // Override with custom defaults
            ...(instanceVal || {}), // Override with instance values
          };
        } else {
          // For scalar types, use existing priority logic
          acc[prop.name] =
            instanceVal !== undefined
              ? instanceVal
              : customVal !== undefined
                ? customVal
                : defaultVal;
        }
        return acc;
      },
      {},
    );
    return result;
  };

  const [options, setOptions] = useState(getInitialOptions());
  const [appearanceConfig, setAppearanceConfig] = useState(
    getInitialAppearanceOptions(),
  );

  useEffect(() => {
    if (visible) {
      setOptions(getInitialOptions());
      setAppearanceConfig(getInitialAppearanceOptions());
    }
  }, [indicatorEntry, visible]);

  const handleChange = (name, value) => {
    setOptions((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAppearanceChange = (propName, field, fieldValue) => {
    setAppearanceConfig((prev) => {
      // Find the property definition to get defaults
      // This ensures we always merge with complete defaults, not partial objects
      const propDef = indicatorEntry.defaultOptionsForAppearance?.find(
        (p) => p.name === propName,
      );
      const defaults = propDef?.defaultValue || {};

      return {
        ...prev,
        [propName]: {
          ...defaults, // Start with registry defaults
          ...(prev[propName] || {}), // Override with current state
          [field]: fieldValue, // Apply the new value
        },
      };
    });
  };

  const handleScalarAppearanceChange = (propName, value) => {
    setAppearanceConfig((prev) => ({
      ...prev,
      [propName]: value,
    }));
  };

  const handleSave = () => {
    onSave(options, appearanceConfig);
    onClose();
  };

  // Save all defaults (config + appearance)
  const handleSaveAllDefaults = () => {
    if (!indicatorEntry?.key) return;

    saveAllDefaults(indicatorEntry.key, options, appearanceConfig);
    toast.current?.show({
      severity: "success",
      summary: "Padrões Salvos",
      detail: `Padrões salvos para ${indicatorEntry.displayName}`,
      life: 3000,
    });
  };

  // Save only appearance defaults
  const handleSaveAppearanceDefaults = () => {
    if (!indicatorEntry?.key) return;

    saveAppearanceDefaults(indicatorEntry.key, appearanceConfig);
    toast.current?.show({
      severity: "success",
      summary: "Aparência Salva",
      detail: `Aparência padrão salva para ${indicatorEntry.displayName}`,
      life: 3000,
    });
  };

  // Get pure registry defaults (ignoring instance config and custom defaults)
  const getRegistryOptions = () => {
    if (
      !indicatorEntry?.defaultOptions ||
      !Array.isArray(indicatorEntry.defaultOptions)
    )
      return {};
    return indicatorEntry.defaultOptions.reduce((acc, prop) => {
      acc[prop.name] = prop.defaultValue;
      return acc;
    }, {});
  };

  const getRegistryAppearanceOptions = () => {
    if (!indicatorEntry?.defaultOptionsForAppearance) return {};
    return indicatorEntry.defaultOptionsForAppearance.reduce((acc, prop) => {
      acc[prop.name] = prop.defaultValue;
      return acc;
    }, {});
  };

  // Reset all defaults (config + appearance)
  const handleResetAllDefaults = () => {
    if (!indicatorEntry?.key) return;

    resetAllDefaults(indicatorEntry.key);

    // Reload options from registry defaults only (ignore instance config)
    setOptions(getRegistryOptions());
    setAppearanceConfig(getRegistryAppearanceOptions());

    toast.current?.show({
      severity: "info",
      summary: "Padrões Resetados",
      detail: `Padrões resetados para ${indicatorEntry.displayName}`,
      life: 3000,
    });
  };

  // Reset only appearance defaults
  const handleResetAppearanceDefaults = () => {
    if (!indicatorEntry?.key) return;

    resetAppearanceDefaults(indicatorEntry.key);

    // Reload appearance from registry defaults only (ignore instance config)
    setAppearanceConfig(getRegistryAppearanceOptions());

    toast.current?.show({
      severity: "info",
      summary: "Aparência Resetada",
      detail: `Aparência padrão resetada para ${indicatorEntry.displayName}`,
      life: 3000,
    });
  };

  if (!indicatorEntry) return null;

  return (
    <>
      <Toast ref={toast} position="top-center" />
      <FormModal
        title={indicatorEntry.displayName}
        visible={visible}
        onHide={onClose}
        hostBody={hostBody}
        onSave={handleSave}
        size="large"
        contentStyle={{ overflow: "hidden" }}
        className="indicator-edit-modal"
        extraActions={
          <div style={{ display: "flex", gap: "3px" }}>
            <button
              type="button"
              className="p-button p-button-sm p-button-outlined"
              onClick={handleSaveAllDefaults}
              title="Salvar todas as opções (config + aparência) como padrão"
              style={{ fontSize: "11px", padding: "4px 8px" }}
              data-evo-id="indicatoreditmodal-btn-save-defaults"
            >
              <i
                className="pi pi-save"
                style={{ marginRight: "3px", fontSize: "11px" }}
              ></i>
              Usar como Padrão
            </button>
            <button
              type="button"
              className="p-button p-button-sm p-button-outlined p-button-secondary"
              onClick={handleResetAllDefaults}
              title="Resetar todos os padrões customizados"
              style={{ fontSize: "11px", padding: "4px 8px" }}
              data-evo-id="indicatoreditmodal-btn-reset-defaults"
            >
              <i
                className="pi pi-refresh"
                style={{ marginRight: "3px", fontSize: "11px" }}
              ></i>
              Original
            </button>
          </div>
        }
      >
        <div className="indicator-modal-scroll">
          <div
            className="indicator-modal-content"
            style={{ padding: "0.375rem 0.625rem" }}
          >
            {/* Configuration Section Header */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "0.375rem",
                paddingBottom: "0.25rem",
                borderBottom: "1px solid var(--surface-border)",
              }}
            >
              <h5
                style={{
                  margin: 0,
                  fontSize: "0.625rem",
                  fontWeight: "600",
                  textTransform: "uppercase",
                  letterSpacing: "0.03em",
                  color: "var(--text-color-secondary)",
                }}
              >
                Configurações
              </h5>
            </div>

            {/* Input options */}
            {indicatorEntry.defaultOptions &&
            Array.isArray(indicatorEntry.defaultOptions) &&
            indicatorEntry.defaultOptions.length > 0 ? (
              indicatorEntry.defaultOptions.map((prop, idx) => {
                try {
                  const enhancedProp = {
                    ...prop,
                    _chartContext: {
                      chartIndicators,
                      currentPanel,
                      excludeIndicatorId: indicatorEntry.id,
                    },
                  };

                  return (
                    <div
                      key={prop.name || idx}
                      className="indicator-modal-field"
                    >
                      <label
                        htmlFor={prop.name}
                        className="indicator-modal-label"
                        style={{
                          minWidth: "5.5rem",
                          marginRight: "0.5rem",
                          textAlign: "right",
                        }}
                      >
                        {prop.displayName}
                      </label>
                      <div style={{ flex: 1 }}>
                        {enhancedProp.render(
                          options,
                          handleChange,
                          enhancedProp._chartContext,
                        )}
                      </div>
                    </div>
                  );
                } catch (error) {
                  return (
                    <div
                      key={prop.name || idx}
                      className="indicator-modal-error"
                    >
                      <strong>Erro ao renderizar opção:</strong> {prop.name}
                      <br />
                      <span>{error.message}</span>
                    </div>
                  );
                }
              })
            ) : (
              <div
                style={{
                  padding: "0.5rem",
                  color: "var(--text-color-secondary)",
                  fontStyle: "italic",
                  fontSize: "11px",
                }}
              >
                Nenhuma configuração disponível
              </div>
            )}

            {/* Appearance Section */}
            {indicatorEntry.defaultOptionsForAppearance && (
              <>
                {/* Appearance Section Header */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginTop: "0.5rem",
                    marginBottom: "0.375rem",
                    paddingBottom: "0.25rem",
                    borderBottom: "1px solid var(--surface-border)",
                  }}
                >
                  <h5
                    style={{
                      margin: 0,
                      fontSize: "0.625rem",
                      fontWeight: "600",
                      textTransform: "uppercase",
                      letterSpacing: "0.03em",
                      color: "var(--text-color-secondary)",
                    }}
                  >
                    Aparência
                  </h5>
                  <div
                    style={{
                      display: "flex",
                      gap: "3px",
                      alignItems: "center",
                    }}
                  >
                    <button
                      type="button"
                      className="p-button p-button-sm p-button-outlined"
                      onClick={handleSaveAppearanceDefaults}
                      title="Salvar apenas as opções de aparência como padrão"
                      style={{ fontSize: "11px", padding: "3px 6px" }}
                      data-evo-id="indicatoreditmodal-btn-save"
                    >
                      <i
                        className="pi pi-palette"
                        style={{ marginRight: "3px", fontSize: "11px" }}
                      ></i>
                      Aparência Padrão
                    </button>
                    <button
                      type="button"
                      className="p-button p-button-sm p-button-outlined p-button-secondary"
                      onClick={handleResetAppearanceDefaults}
                      title="Resetar apenas os padrões de aparência"
                      style={{ fontSize: "11px", padding: "3px 6px" }}
                      data-evo-id="indicatoreditmodal-btn-edit-appearance"
                    >
                      <i
                        className="pi pi-refresh"
                        style={{ marginRight: "3px", fontSize: "11px" }}
                      ></i>
                      Original
                    </button>
                  </div>
                </div>

                <div className="indicator-modal-appearance-section">
                  {indicatorEntry.defaultOptionsForAppearance.map(
                    (prop, idx) => {
                      try {
                        const isScalarProperty =
                          prop.type === "number" ||
                          prop.type === "boolean" ||
                          prop.type === "thresholdLine" ||
                          prop.type === "opacity";

                        const value =
                          prop.type === "thresholdLine"
                            ? () =>
                                appearanceConfig[prop.name] !== undefined
                                  ? appearanceConfig[prop.name]
                                  : prop.defaultValue
                            : isScalarProperty
                              ? appearanceConfig[prop.name] !== undefined
                                ? appearanceConfig[prop.name]
                                : prop.defaultValue
                              : appearanceConfig[prop.name] ||
                                prop.defaultValue ||
                                {};

                        const changeHandler = isScalarProperty
                          ? prop.type === "thresholdLine"
                            ? (_thresholdName, newValue) => {
                                handleScalarAppearanceChange(
                                  prop.name,
                                  newValue,
                                );
                              }
                            : (_field, fieldValue) =>
                                handleScalarAppearanceChange(
                                  prop.name,
                                  fieldValue,
                                )
                          : (field, fieldValue) =>
                              handleAppearanceChange(
                                prop.name,
                                field,
                                fieldValue,
                              );

                        return (
                          <div
                            key={prop.name || idx}
                            className="indicator-modal-field"
                            style={{
                              display: "flex",
                              flexDirection: "row",
                              alignItems: "center",
                              marginBottom: "0.5rem",
                            }}
                          >
                            <label
                              htmlFor={prop.name}
                              className="indicator-modal-label"
                              style={{
                                minWidth: "7rem",
                                marginRight: "0.75rem",
                                textAlign: "right",
                                fontSize: "12px",
                              }}
                            >
                              {prop.displayName}
                            </label>
                            <div style={{ flex: 1 }}>
                              {prop.render(value, changeHandler)}
                            </div>
                          </div>
                        );
                      } catch (error) {
                        return (
                          <div
                            key={prop.name || idx}
                            className="indicator-modal-error"
                          >
                            <strong>Erro ao renderizar aparência:</strong>{" "}
                            {prop.name}
                            <br />
                            <span>{error.message}</span>
                          </div>
                        );
                      }
                    },
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </FormModal>
    </>
  );
};

IndicatorEditModal.propTypes = {
  indicatorEntry: PropTypes.shape({
    displayName: PropTypes.string.isRequired,
    config: PropTypes.object,
    appearanceConfig: PropTypes.object,
    defaultOptions: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired,
        displayName: PropTypes.string,
        defaultValue: PropTypes.any,
        render: PropTypes.func.isRequired,
      }),
    ).isRequired,
    defaultOptionsForAppearance: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired,
        displayName: PropTypes.string,
        defaultValue: PropTypes.any,
        render: PropTypes.func.isRequired,
      }),
    ),
  }),
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  hostBody: PropTypes.object,
};

export default React.memo(IndicatorEditModal);
